(ns main.events
  (:require
   ["moment" :as moment]
   [very.re-frame.core :as rf]
   [runtime.core :refer [DEBUG]]
   [graphql.core :as qu]
   [goog.events]
   [oops.core :refer [oget]]
   [util.core :refer [get-path-with-query]]
   [router.core :as router]
   [css.core :as css]))

;; 15 minutes
(def *inactivity-timeout-ms* (if DEBUG
                               (* 24 60 60 1000)
                               (* 15 60 1000)))

(def *5-minutes* (* 5 60 1000))

(def *max-fetch-attempts* 39)

(rf/reg-event-db
  :noop
  identity)

(defn- impersonate-path?
  "Returns true if the given url is an impersonation attempt, meaning there's a
  known impersonation path followed by a session hash. e.g. advisor/HX19JLO"
  [loc]
  (boolean (some #(and (= % (first (:paths loc)))
                       (not-empty (second (:paths loc))))
                 ["personal-gameplan"
                  "advisor"])))

(rf/reg-event-fx
  :initialize-db
  [(rf/inject-cofx :domain)]
  (fn [cfx]
    {:db (assoc (:db cfx)
                :route-table router/route-table
                :authentication-status :unauthenticated)
     :entity [{:type :partner
               :op :get
               :data {:domain (:domain cfx)}}
              {:type :brand
               :op :get
               :data {:domain (:domain cfx)}
               :success [:branding-fetched]
               :error [:branding-errored]}]}))

(rf/reg-event-fx
 :branding-fetched
 (fn [cfx [data]]
   (let [brand (:brand data)
         theme {:primary (:primary-color brand)
                :secondary (:secondary-color brand)
                :tertiary (:tertiary-color brand)}]
     ;; this is really side-effecty but we only do it once, ever
     (css.core/set-theme! theme)

     ;; we have two branches into the app past this point - either we're
     ;; attempting an impersonation, or we're trying to navigate into the app.
     ;; in the first path we have to use the url params to authenticate against
     ;; a valid session. in the second path we can look up the existing browser
     ;; session, then flow into the app normally.
     (let [loc (router/parse-location (get-path-with-query))]
       (if (impersonate-path? loc)
         {:dispatch [:impersonate/identify-session loc]}
         {:dispatch [:validate-session (:current loc)]})))))

(rf/reg-event-fx
 :branding-errored
 (fn [cfx _]
   {:nav! "/unavailable"}))

(rf/reg-event-fx
 :service-error
 (fn [_]
   {:nav! :kill}))

(rf/reg-event-fx
  :validate-session
  (fn [cfx [nav-tar]]
    ;; checks if a user is valid based on the stored browser session token.
    ;; nav-tar is the route to navigate to after a successful auth response.
    {:db (assoc-in (:db cfx) [:main :navigation-target] nav-tar)
     :graphql {:gql (qu/session)
               :success [:authenticated]
               :error [:unauthenticated]}}))

(rf/reg-event-fx
 :authenticated
 (fn [cfx [{:keys [client]}]]
   {:db (-> (:db cfx)
            (assoc :authentication-status :authenticated)
            (assoc-in [:session :client] client))
    :sentry {:set-user client}
    :inactivity {:duration *inactivity-timeout-ms*
                 :on-expiration [:session-expired]}
    :dispatch [:init-blocking-flow]}))


;; the blocking flow is a (silly) business requirement; users should not be able
;; to view the app with stale data. this means that for the first visit of the
;; day (all our data is updated on a daily basis) users need to see a global
;; spinner until all our data has been updated. the blocking flow accompishes
;; this by checking for the oldest account updated-at date in a recursive poll.
(rf/reg-event-fx
  :init-blocking-flow
  (fn [cfx]
    (let [nxt (get-in cfx [:db :main :navigation-target])]
      {:db (update (:db cfx) :main dissoc :navigation-target)
       :dispatch [:maybe-block-for-latest]
       :async-flow {:id :validate-session/reroute
                    :rules [{:when :seen-any-of?
                             :events [:accounts-updated
                                      :continue-with-stale]
                             :dispatch [:main/unblocked nxt]
                             :halt? true}]}})))

(rf/reg-event-fx
  :main/unblocked
  (fn [cfx [nxt]]
    {:nav! nxt}))

(rf/reg-event-db
  ;; this is to satisfy re-frame. we only use accounts updated as a notification
  :accounts-updated
  identity)

(when DEBUG
  ;; skip this shenanigans when working locally
  (rf/reg-event-fx
   :init-blocking-flow
   (fn [cfx]
     {:nav! (get-in cfx [:db :main :navigation-target])
      :db (assoc (:db cfx) :application-loader {:loaded? true})})))

(rf/reg-sub
 :application-loader
 (fn [db]
   (:application-loader db)))

(rf/reg-event-fx
 :maybe-block-for-latest
 (fn [cfx]
   {:graphql {:gql (qu/last-updated)
              :success [:last-updated-fetched]
              :error [:last-updated-check-failed]
              :failure [:last-updated-check-failed]}
    :db (update-in (:db cfx) [:application-loader :fetch-attempts] (fnil inc -1))}))

(defn- more-than-a-day-ago? [dt]
  (let [last-updated (moment dt)
        yesterday (.subtract (.startOf (.utc moment) "day") 1 "day")]
    (.isBefore last-updated yesterday)))

(defn- poll-fx [state latest]
  (let [attempts (:fetch-attempts state)
        [fx trigger?]
        (cond
          ;; if we've exhausted fetch attempts and the data STILL isn't there, let
          ;; the user know
          (> attempts *max-fetch-attempts*)
          [{:dispatch [:unable-to-update-accounts]} false]

          ;; when null, an account has NEVER been updated, so we have to block
          ;; until all the latest data is fetched...
          (nil? latest)
          [{:dispatch-later [{:ms 3000 :dispatch [:maybe-block-for-latest]}]
            :db (assoc state :loaded? false)} true]

          ;; last updated more than a day ago, also block until fetched
          (more-than-a-day-ago? latest)
          [{:dispatch-later [{:ms 3000 :dispatch [:maybe-block-for-latest]}]
            :db (assoc state :loaded? false)} true]

          ;; less than a day ago, let people in immediately
          (not (more-than-a-day-ago? latest))
          [{:db (assoc state :loaded? true)
            :dispatch [:accounts-updated]} false])

        ;; trigger a call to update the accounts on the first invocation
        fetch (when (and trigger? (= attempts 0))
                {:graphql {:gql (qu/update-accounts)
                           :success [:noop]}})]
    (merge fx fetch)))

(defn- poll-for-latest
  "Generate the appropriate fx from polling, then insert any db results into the
  proper place in the db. This keeps our impl of poll-fx much cleaner."
  [cfx latest]
  (let [fx (poll-fx (get-in cfx [:db :application-loader]) latest)
        db (:db fx)]
    (merge fx (when db {:db (assoc (:db cfx) :application-loader db)}))))

(rf/reg-event-fx
 :last-updated-fetched
 (fn [cfx [data]]
   (poll-for-latest cfx (get-in data [:client :accounts :last-updated]))))

(rf/reg-event-fx
 :last-updated-check-failed
 (fn [cfx]
   (poll-for-latest cfx nil)))

(rf/reg-event-db
 :unable-to-update-accounts
 (rf/path :application-loader)
 (fn [db]
   {:loaded? false
    :fetch-error? true}))

(rf/reg-event-fx
 :retry-latest
 (fn [cfx]
   {:db (-> (:db cfx)
            (assoc-in [:application-loader :fetch-error?] false)
            (assoc-in [:application-loader :fetch-attempts] -1))
    :dispatch [:maybe-block-for-latest]}))

(rf/reg-event-db
 :continue-with-stale
 (rf/path :application-loader)
 (fn [db]
   {:loaded? true
    :fetch-error? false}))

(rf/reg-event-fx
  :unauthenticated
  (fn [cfx]
    (let [intent (get-path-with-query)]
      {:db (assoc (:db cfx) :authentication-status :unauthenticated)
       :nav! intent})))

(rf/reg-event-fx
 :session-expired
 (fn [cfx]
   {:alert! {:message "To protect your security and privacy, you have been logged out automatically due to inactivity."
             :after [:log-out]}}))

(rf/reg-event-fx
 :log-out
 (fn [cfx]
   {:db (assoc (:db cfx) :authentication-status :unauthenticated)
    :session [:remove :token]
    :dispatch [:reload]}))

(rf/reg-event-fx
 :reload
 (fn [cfx]
   (.reload (oget js/window "location") true)))

(rf/reg-event-fx
 :navigate-to-context
 (fn [cfx [ctx]]
   {:nav! ctx}))

(rf/reg-sub
 :authentication-status
 (fn [db]
   (:authentication-status db)))

(rf/reg-sub
 :clients
 (fn [db]
   (:clients db)))

(rf/reg-sub
 :client
 (fn [db]
   (get-in db [:session :client])))
